import React, { useMemo, useState } from 'react';
import { Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export interface PendulumReading {
  id: string;
  lengthCm: number;
  oscillations: number;
  totalTimeS: number;
  angleDeg: number;
  massG: number;
  periodS: number;
  periodSquaredS2: number;
  remarks: string;
}

interface PendulumNotebookProps {
  readings: PendulumReading[];
  onChange: (readings: PendulumReading[]) => void;
  onAutoRecord: () => PendulumReading | null;
  onExportCsv?: () => void;
  onExportPdf?: () => void;
}

const emptyReading: PendulumReading = {
  id: `${Date.now()}`,
  lengthCm: 50,
  oscillations: 20,
  totalTimeS: 0,
  angleDeg: 5,
  massG: 100,
  periodS: 0,
  periodSquaredS2: 0,
  remarks: '',
};

const PendulumNotebook: React.FC<PendulumNotebookProps> = ({ readings, onChange, onAutoRecord, onExportCsv, onExportPdf }) => {
  const [draft, setDraft] = useState<PendulumReading>(emptyReading);

  const nextPeriod = (totalTimeS: number, oscillations: number) => (oscillations > 0 ? totalTimeS / oscillations : 0);

  const addManualReading = () => {
    const record = {
      ...draft,
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      periodS: nextPeriod(draft.totalTimeS, draft.oscillations),
      periodSquaredS2: (nextPeriod(draft.totalTimeS, draft.oscillations) ** 2),
    };
    onChange([...readings, record]);
    setDraft({ ...emptyReading, id: `${Date.now()}` });
  };

  const updateReading = (id: string, field: keyof PendulumReading, value: string | number) => {
    const updated = readings.map((row) => {
      if (row.id !== id) return row;
      const nextRow = { ...row, [field]: value } as PendulumReading;
      const period = nextPeriod(nextRow.totalTimeS, nextRow.oscillations);
      return {
        ...nextRow,
        periodS: period,
        periodSquaredS2: period ** 2,
      };
    });
    onChange(updated);
  };

  const deleteReading = (id: string) => {
    onChange(readings.filter((row) => row.id !== id));
  };

  const autoRecord = () => {
    const record = onAutoRecord();
    if (!record) return;
    onChange([...readings, record]);
  };

  const exportCsv = () => {
    const csv = [
      ['S.No.', 'Length L (cm)', 'n', 't (s)', 'T = t/n (s)', 'T² (s²)', 'θ (°)', 'Mass (g)', 'Remarks'],
      ...readings.map((row, index) => [
        index + 1,
        row.lengthCm,
        row.oscillations,
        row.totalTimeS,
        row.periodS,
        row.periodSquaredS2,
        row.angleDeg,
        row.massG,
        row.remarks,
      ]),
    ].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'pendulum-notebook.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const readOnlyRows = useMemo(() => readings.length === 0 ? [{ ...emptyReading, id: 'empty' }] : readings, [readings]);

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Laboratory notebook</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={autoRecord}>Auto-record</Button>
          <Button variant="outlined" onClick={exportCsv}>CSV</Button>
          <Button variant="outlined" onClick={onExportPdf}>PDF</Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="Length (cm)" type="number" value={draft.lengthCm} onChange={(e) => setDraft({ ...draft, lengthCm: Number(e.target.value) })} />
        <TextField label="Oscillations" type="number" value={draft.oscillations} onChange={(e) => setDraft({ ...draft, oscillations: Number(e.target.value) })} />
        <TextField label="Total time (s)" type="number" value={draft.totalTimeS} onChange={(e) => setDraft({ ...draft, totalTimeS: Number(e.target.value) })} />
        <TextField label="Angle (°)" type="number" value={draft.angleDeg} onChange={(e) => setDraft({ ...draft, angleDeg: Number(e.target.value) })} />
        <TextField label="Mass (g)" type="number" value={draft.massG} onChange={(e) => setDraft({ ...draft, massG: Number(e.target.value) })} />
        <Button variant="contained" onClick={addManualReading}>Add row</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>S.No.</TableCell>
              <TableCell>Length L (cm)</TableCell>
              <TableCell>n</TableCell>
              <TableCell>t (s)</TableCell>
              <TableCell>T = t/n (s)</TableCell>
              <TableCell>T² (s²)</TableCell>
              <TableCell>θ (°)</TableCell>
              <TableCell>Mass (g)</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {readOnlyRows.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField value={row.lengthCm} onChange={(e) => updateReading(row.id, 'lengthCm', Number(e.target.value))} size="small" />
                </TableCell>
                <TableCell>
                  <TextField value={row.oscillations} onChange={(e) => updateReading(row.id, 'oscillations', Number(e.target.value))} size="small" />
                </TableCell>
                <TableCell>
                  <TextField value={row.totalTimeS} onChange={(e) => updateReading(row.id, 'totalTimeS', Number(e.target.value))} size="small" />
                </TableCell>
                <TableCell>{row.periodS.toFixed(3)}</TableCell>
                <TableCell>{row.periodSquaredS2.toFixed(3)}</TableCell>
                <TableCell>
                  <TextField value={row.angleDeg} onChange={(e) => updateReading(row.id, 'angleDeg', Number(e.target.value))} size="small" />
                </TableCell>
                <TableCell>
                  <TextField value={row.massG} onChange={(e) => updateReading(row.id, 'massG', Number(e.target.value))} size="small" />
                </TableCell>
                <TableCell>
                  <TextField value={row.remarks} onChange={(e) => updateReading(row.id, 'remarks', e.target.value)} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => deleteReading(row.id)} aria-label="Delete row">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PendulumNotebook;
